import { readFileSync, readdirSync, statSync } from 'fs';
import path from 'path';

import { useMemo } from 'react';

import { compile } from '@mdx-js/mdx';
import * as mdx from '@mdx-js/react';
import imageSize from 'rehype-img-size';
import remarkFrontmatter from 'remark-frontmatter';
import remarkGfm from 'remark-gfm';
import remarkMdxFrontmatter from 'remark-mdx-frontmatter';

import { useLocalStorage } from 'usehooks-ts';

import { jsxRuntime } from '@lib/jsx-runtime.cjs';

export const getInterviewQuestionIds = () => {
    const interviewQuestionsDir = 'interviewQuestions';

    return readdirSync(interviewQuestionsDir).filter((entry) => {
        const entryPath = path.join(interviewQuestionsDir, entry);
        return statSync(entryPath).isDirectory();
    });
};

export const getInterviewQuestions = async (locale: string) => {
    const questionIds = getInterviewQuestionIds();

    return await Promise.all(
        questionIds.map(async (questionId) => {
            const file = readFileSync(
                path.resolve(
                    process.cwd(),
                    `interviewQuestions/${questionId}/${locale}.mdx`,
                ),
            );

            const content = await compile(file, {
                // @ts-ignore
                remarkPlugins: [remarkGfm, remarkFrontmatter, remarkMdxFrontmatter],
                // @ts-ignore
                rehypePlugins: [[imageSize, { dir: 'public' }]],
                outputFormat: 'function-body',
                providerImportSource: '@mdx-js/react',
                useDynamicImport: true,
                format: 'mdx',
            });

            const { frontmatter } = generateJSXFromMDXModule(String(content));

            return {
                questionId,
                title: frontmatter.meta?.title || '',
                solutionMdxSource: String(content),
            };
        }),
    );
};

export const useInterviewQuestionComponent = (mdxModule: string) => {
    return useMemo(() => generateJSXFromMDXModule(mdxModule), [mdxModule]);
};

export const compileInterviewQuestionMDXFromFile = async (params: {
    id: string;
    locale: string;
}) => {
    const { id, locale } = params;

    const file = readFileSync(
        path.resolve(process.cwd(), `interviewQuestions/${id}/${locale}.mdx`),
    );

    const content = await compile(file, {
        // @ts-ignore
        remarkPlugins: [remarkGfm, remarkFrontmatter, remarkMdxFrontmatter],
        // @ts-ignore
        rehypePlugins: [[imageSize, { dir: 'public' }]],
        outputFormat: 'function-body',
        providerImportSource: '@mdx-js/react',
        useDynamicImport: true,
        format: 'mdx',
    });

    return content;
};

export const generateJSXFromMDXModule = (
    mdxModuleContent: string,
): {
    Content: React.ElementType;
    frontmatter: { meta?: { title?: string; description?: string } };
} => {
    const fullScope = Object.assign({ opts: { ...mdx, ...jsxRuntime } });

    const keys = Object.keys(fullScope);
    const values = Object.values(fullScope);

    const hydrateFn = Reflect.construct(Function, keys.concat(`${mdxModuleContent}`));
    const data = hydrateFn.apply(hydrateFn, values);

    return { Content: data.default, frontmatter: data.frontmatter || {} };
};

const useLearnedQuestions = () => {
    const [learnedQuestions, setLearnedQuestions] = useLocalStorage<string[]>(
        'learned_questions',
        [],
    );

    const values = useMemo(() => {
        return Array.from(new Set(learnedQuestions));
    }, [learnedQuestions]);

    const setValues = (values: string[] = []) => {
        setLearnedQuestions(Array.from(new Set(values)));
    };

    return [values, setValues] as const;
};

export { useLearnedQuestions };
